import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, getHttpErrorStatus, logHttpError, storeAccessToken } from '../services/api';

interface LoginPageProps {
  onLoginSuccess: (id: string, isAdmin: boolean) => void;
}

interface AuthResponse {
  id: number;
  is_admin?: boolean;
  access_token?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [secondFactorCode, setSecondFactorCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const mountedRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const requestControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
      requestControllerRef.current?.abort();
    };
  }, []);

  const beginRequest = () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    requestControllerRef.current = controller;
    return { controller, generation };
  };

  const requestIsCurrent = (generation: number, controller: AbortController) => (
    mountedRef.current
    && requestGenerationRef.current === generation
    && !controller.signal.aborted
  );

  const finishLogin = (data: AuthResponse) => {
    if (!data.access_token) throw new Error('Missing access token');

    const userId = data.id.toString();
    const isAdmin = Boolean(data.is_admin);
    localStorage.setItem('sharkly_user_id', userId);
    localStorage.setItem('sharkly_is_admin', isAdmin.toString());
    storeAccessToken(data.access_token);
    setChallengeToken(null);
    setSecondFactorCode('');
    onLoginSuccess(userId, isAdmin);
    navigate('/feed');
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!username || !password) {
      setError('Введите никнейм и пароль');
      return;
    }

    setIsLoading(true);
    setError(null);
    const { controller, generation } = beginRequest();
    try {
      const response = await api.post('/auth/login', { username, password }, { signal: controller.signal });
      if (!requestIsCurrent(generation, controller)) return;
      if (response.status === 202 && response.data.requires_2fa && response.data.challenge_token) {
        setChallengeToken(response.data.challenge_token);
        setPassword('');
        return;
      }
      finishLogin(response.data);
    } catch (loginError: unknown) {
      if (!requestIsCurrent(generation, controller)) return;
      logHttpError('Ошибка входа', loginError);
      const status = getHttpErrorStatus(loginError);
      if (status === 404) setError('Сервис входа временно недоступен');
      else if (status === 401) setError('Неверный никнейм или пароль');
      else setError('Ошибка соединения с сервером');
    } finally {
      if (requestIsCurrent(generation, controller)) setIsLoading(false);
    }
  };

  const handleSecondFactor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challengeToken || !secondFactorCode.trim()) {
      setError('Введите код 2FA или резервный код');
      return;
    }

    setIsLoading(true);
    setError(null);
    const { controller, generation } = beginRequest();
    try {
      const response = await api.post('/auth/login/2fa', {
        challenge_token: challengeToken,
        code: secondFactorCode.trim(),
      }, { signal: controller.signal });
      if (!requestIsCurrent(generation, controller)) return;
      finishLogin(response.data);
    } catch (secondFactorError: unknown) {
      if (!requestIsCurrent(generation, controller)) return;
      logHttpError('Ошибка второго этапа входа', secondFactorError);
      setError(
        getHttpErrorStatus(secondFactorError) === 401
          ? 'Неверный код 2FA или резервный код'
          : 'Не удалось подтвердить вход. Попробуйте ещё раз',
      );
    } finally {
      if (requestIsCurrent(generation, controller)) setIsLoading(false);
    }
  };

  const cancelSecondFactor = () => {
    requestControllerRef.current?.abort();
    requestGenerationRef.current += 1;
    setChallengeToken(null);
    setSecondFactorCode('');
    setError(null);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-[#0f1115]">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase">
          SHARKLY 🦈
        </h1>
        <p className="text-gray-400 text-xs mt-1">
          {challengeToken ? 'Подтвердите вход' : 'С возвращением в стаю'}
        </p>
      </div>

      {challengeToken ? (
        <form className="w-full max-w-md space-y-4 mb-8" onSubmit={handleSecondFactor}>
          <p className="text-gray-400 text-sm text-center">
            Введите код из приложения-аутентификатора или один резервный код.
          </p>
          <label className="sr-only" htmlFor="second-factor-code">Код 2FA или резервный код</label>
          <input
            id="second-factor-code"
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            aria-label="Код 2FA или резервный код"
            placeholder="Код 2FA или резервный код"
            value={secondFactorCode}
            onChange={(event) => setSecondFactorCode(event.target.value)}
            className="w-full p-4 rounded-xl bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none transition"
            disabled={isLoading}
            autoFocus
          />
          <button
            type="submit"
            disabled={isLoading || !secondFactorCode.trim()}
            className="w-full px-12 py-3 rounded-full font-bold transition-all shadow-[0_0_20px_rgba(37,99,235,0.4)] bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? 'Проверка...' : 'Подтвердить'}
          </button>
          <button
            type="button"
            onClick={cancelSecondFactor}
            className="w-full px-6 py-2 text-gray-400 hover:text-white rounded-xl transition"
          >
            Назад
          </button>
        </form>
      ) : (
        <form className="w-full max-w-md space-y-4 mb-8" onSubmit={handleLogin}>
          <label className="sr-only" htmlFor="login-username">Никнейм</label>
          <input
            id="login-username"
            type="text"
            aria-label="Никнейм"
            autoComplete="username"
            placeholder="Никнейм"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            className="w-full p-4 rounded-xl bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none transition"
            disabled={isLoading}
          />
          <label className="sr-only" htmlFor="login-password">Пароль</label>
          <input
            id="login-password"
            type="password"
            aria-label="Пароль"
            autoComplete="current-password"
            placeholder="Пароль"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full p-4 rounded-xl bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none transition"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-12 py-3 rounded-full font-bold transition-all shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:-translate-y-1 active:scale-95 bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? 'Проверка...' : 'Войти'}
          </button>
        </form>
      )}

      {error && (
        <p role="alert" className="text-red-500 text-sm mb-4 text-center bg-red-500/10 px-4 py-2 rounded-lg border border-red-500/20">
          {error}
        </p>
      )}

      {!challengeToken && (
        <p className="mt-2 text-gray-500 text-sm">
          Нет аккаунта?{' '}
          <button
            type="button"
            onClick={() => !isLoading && navigate('/')}
            className="text-blue-400 hover:underline hover:text-blue-300 transition"
          >
            Зарегистрироваться
          </button>
        </p>
      )}
    </div>
  );
};

export default LoginPage;
